
import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { fetchVisaStats } from "../api";

export default function BarGraph() {
  const [visaData, setVisaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("pie");
  const [range, setRange] = useState({
    start: "",
    end: "",
  });

  const VISA_STATUSES = ["Student", "PSW", "COS", "Leave to remain", "Permanent"];
  const CUSTOM_COLORS = ["#E97A2B","#10b981", "#3b82f6", "#E6E1DC", "#F7C66A"];

  const getColorForStatus = (statusId) => {
    const index = VISA_STATUSES.indexOf(statusId);
    return index !== -1 ? CUSTOM_COLORS[index] : "#6b7280";
  };

  // 🧭 Fetch Visa Stats
  const loadVisaStats = async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.start && range.end) {
        params.start = range.start;
        params.end = range.end;
      }
      const res = await fetchVisaStats(params);
      console.log("res",res);
      const rawData = res?.data?.data || [];

      const processed = rawData
        .map((d) => ({ ...d, _id: d._id || "Unknown" }))
        .filter((d) => d.totalHours > 0 || d.count > 0);

      setVisaData(processed);
    } catch (err) {
      console.error("Error fetching visa stats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVisaStats();
  }, []); // fetch initially

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setRange((prev) => ({ ...prev, [name]: value }));
  };

  const handleApply = () => {
    if (range.start && range.end) {
      loadVisaStats();
    } else {
      alert("Please select both start and end dates.");
    }
  };

  // CSV Download
  const handleDownloadReport = () => {
    if (!visaData || visaData.length === 0) {
      alert("No data available to export.");
      return;
    }

    const rows = [];
    visaData.forEach((group) => {
      group.users.forEach((user) => {
        rows.push({
          Status: group._id || "Unknown",
          Name: user.name,
          "Working Hours": user.hoursWorked,
          Locations: user.locations?.length ? user.locations.join(", ") : "-",
        });
      });
    });

    const header = Object.keys(rows[0]).join(",");
    const csv = [
      header,
      ...rows.map((r) => Object.values(r).map((v) => `"${v}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Visa_Report_${range.start || "unknown"}_to_${range.end || "unknown"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const legendPayload = visaData.map((d) => ({
    value: d._id,
    id: d._id,
    type: "square",
    color: getColorForStatus(d._id),
  }));

  const CHART_HEIGHT = 160;

  return (
    <div className="p-4 bg-white rounded-xl shadow-sm w-full max-w-2xl mx-auto">
      {/* Header with Date Range */}
      <div className="mb-4">
  {/* Title */}
  <h2 className="text-lg font-semibold text-gray-800 mb-2 text-center md:text-left">
    Staff Hours
  </h2>

  {/* Date Range Row */}
  <div className="flex flex-wrap justify-center md:justify-start items-center gap-1">
    <input
      type="date"
      name="start"
      value={range.start}
      onChange={handleDateChange}
      className="border rounded-md px-1 py-0.5 text-xs w-[110px]"
    />
    <span className="text-gray-500 text-xs">to</span>
    <input
      type="date"
      name="end"
      value={range.end}
      onChange={handleDateChange}
      className="border rounded-md px-1 py-0.5 text-xs w-[110px]"
    />
    <button
      onClick={handleApply}
      className="bg-indigo-600 text-white rounded-md text-xs px-2 py-1 hover:bg-indigo-700"
    >
      Apply
    </button>
  </div>
</div>


      {/* Toggle + Download */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center space-x-2 text-sm">
          <button
            onClick={() => setActiveView("pie")}
            className={`px-3 py-1 rounded-full ${
              activeView === "pie"
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            Pie
          </button>
          <button
            onClick={() => setActiveView("bar")}
            className={`px-3 py-1 rounded-full ${
              activeView === "bar"
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            Bar
          </button>
        </div>

        <button
          onClick={handleDownloadReport}
          className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
        >
           Report
        </button>
      </div>

      {/* Charts */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <p className="text-gray-500">Loading Visa Stats...</p>
        </div>
      ) : visaData.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-6">
          No data available for selected range.
        </p>
      ) : activeView === "pie" ? (
<div className="p-3 rounded-lg border border-gray-100 bg-gray-50">
  <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
    <PieChart>
      <Tooltip
        formatter={(value, name, props) => [
          `${value.toFixed(2)} Hrs`,
          props.payload._id,
        ]}
        contentStyle={{ border: "1px solid #ccc", fontSize: "12px" }}
      />

      {/* Rounded Pie */}
      <Pie
        data={visaData}
        dataKey="totalHours"
        nameKey="_id"
        cx="50%"
        cy="50%"
        outerRadius={80}
        innerRadius={45}           
        paddingAngle={0}           
        label={({ percent }) =>
          percent > 0.01 ? `${(percent * 100).toFixed(0)}%` : ""
        }
      >
        {visaData.map((entry, i) => (
          <Cell
            key={`pie-${i}`}
            fill={getColorForStatus(entry._id)}
            stroke={getColorForStatus(entry._id)}
            strokeWidth={0.5}
          />
        ))}
      </Pie>

      {/* Center Text */}
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="middle"
        className="text-gray-800"
        style={{ fontSize: "16px" }}
      >
        {`${visaData.reduce((acc, d) => acc + d.totalHours, 0).toFixed(2)} Hrs`}
      </text>
    </PieChart>
  </ResponsiveContainer>
</div>


      ) : (
        <div className="p-3 rounded-lg border border-gray-100 bg-gray-50">
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart
              data={visaData}
              margin={{ top: 5, right: 10, left: -10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
              <XAxis
                dataKey="_id"
                tickLine={false}
                axisLine={false}
                style={{ fontSize: "10px" }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                style={{ fontSize: "10px" }}
              />
              <Tooltip
                cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
                contentStyle={{ border: "1px solid #ccc", fontSize: "12px" }}
                formatter={(v) => [`${v.toFixed(2)} Hrs`, "Total Hours"]}
              />
              <Legend
                payload={legendPayload}
                iconSize={8}
                wrapperStyle={{ fontSize: "11px" }}
              />
              <Bar dataKey="totalHours" radius={[4, 4, 0, 0]} barSize={18}>
                {visaData.map((entry, i) => (
                  <Cell key={`bar-${i}`} fill={getColorForStatus(entry._id)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

